import { useState } from 'react';

const NEWSLETTER_FORM_URL = 'https://readdy.ai/api/form/d9bn9ruc87cp3jcuj360';

export default function FooterSection() {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypotEl = form.querySelector<HTMLInputElement>('[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setSubmitStatus('success');
      setEmail('');
      return;
    }
    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        urlEncoded.append(key, value as string);
      });
      const response = await fetch(NEWSLETTER_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      if (response.ok && parsed?.code === 'OK') {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
        setFormError(parsed?.meta?.message || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
      }
    } catch {
      setSubmitStatus('error');
      setFormError('Netzwerkfehler. Bitte überprüfen Sie Ihre Verbindung.');
    }
  };

  const footerLinks = {
    Kategorien: ['Schuhe', 'Hoodies & Sweater', 'T-Shirts', 'Jacken'],
    Guides: ['Einsteiger Guide', 'Zoll & Steuern', 'QC-Prozess', 'Versand'],
    Rechtliches: ['Impressum', 'Datenschutz', 'AGB', 'Widerruf'],
    Kontakt: ['Über uns', 'Support', 'Partner', 'Presse'],
  };

  return (
    <footer className="bg-foreground-900 text-background-50">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]">
          <img
            src="https://readdy.ai/api/search-image?query=Subtle%20elegant%20abstract%20geometric%20line%20pattern%20in%20very%20light%20warm%20beige%20tones%20on%20a%20dark%20charcoal%20background%20minimal%20decorative%20design%20for%20sophisticated%20website%20footer%20textile%20inspired%20organic%20flowing%20shapes%20delicate%20and%20understated&width=1200&height=600&seq=footer-pattern-v2&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-14 md:py-16">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            <div className="lg:w-2/5">
              <h3 className="text-xl md:text-2xl font-heading font-semibold text-white mb-4">
                oopbuyspread<span className="text-primary-400">.</span>living
              </h3>

              <form id="newsletter-form" data-readdy-form="" onSubmit={handleNewsletterSubmit} className="flex gap-2 mb-5 max-w-sm">
                <input
                  name="phone_alt"
                  type="text"
                  className="honeypot-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Ihre E-Mail-Adresse"
                  required
                  className="flex-1 px-4 py-2.5 text-sm bg-foreground-800 border border-foreground-700 rounded-lg text-white placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                />
                <button
                  type="submit"
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500 hover:bg-primary-600 transition-colors flex-shrink-0 cursor-pointer"
                  aria-label="Newsletter abonnieren"
                >
                  <i className="ri-notification-3-line text-white"></i>
                </button>
              </form>
              {submitStatus === 'success' && (
                <p className="text-sm text-primary-400 mb-4">Vielen Dank! Sie haben sich erfolgreich angemeldet.</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-sm text-accent-400 mb-4">{formError}</p>
              )}

              <p className="text-lg md:text-xl font-heading text-white/80 leading-relaxed">
                Ihr smarter Begleiter<br />für den internationalen Einkauf.
              </p>
            </div>

            <div className="lg:w-3/5 grid grid-cols-2 sm:grid-cols-4 gap-6 md:gap-8">
              {Object.entries(footerLinks).map(([category, links]) => (
                <div key={category}>
                  <h4 className="text-sm font-semibold text-white mb-3">{category}</h4>
                  <ul className="space-y-2">
                    {links.map((link) => (
                      <li key={link}>
                        <a
                          href={`/${link.toLowerCase().replace(/\s+/g, '-')}`}
                          className="text-sm text-foreground-400 hover:text-white transition-colors cursor-pointer"
                        >
                          {link}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 pt-6 border-t border-foreground-800 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-foreground-500">
              &copy; 2026 oopbuyspread.living. Alle Rechte vorbehalten.
            </p>
            <div className="flex items-center gap-3">
              {['ri-twitter-x-line', 'ri-instagram-line', 'ri-youtube-line', 'ri-telegram-line'].map((icon) => (
                <a
                  key={icon}
                  href="#"
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-foreground-700 text-foreground-400 hover:text-white hover:border-foreground-500 transition-colors cursor-pointer"
                  aria-label={icon.replace('ri-', '').replace('-line', '')}
                >
                  <i className={`${icon} text-sm`}></i>
                </a>
              ))}
            </div>
            <a href="/datenschutz" className="text-xs text-foreground-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
              Datenschutz
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}